import {
	GQLOrderFragment,
	GQLOrderItemTileFragment,
	GQLOrderStatusType,
	GQLOrderTrackingFragment,
	GQLOrderTrackingTypeType,
} from '@theprintbar/graphql';
import { orderItemIsExternallyFulfilled, orderItemIsTrends } from './orderItemChecks';

export interface OrderShipmentGroup {
	//Unique per group: the Fulfill Engine shipment id, or the type for the outsourced groups
	key: string;
	type: GQLOrderTrackingTypeType;
	//Only set for warehouse groups, and empty until the order has been pushed
	shipmentId: string;
	//The line items in this parcel. An item split across parcels appears in each of them, carrying
	//only the sizes and quantities that parcel holds and its share of the price.
	items: GQLOrderItemTileFragment[];
	tracking?: GQLOrderTrackingFragment;
	//Groups made up entirely of cancelled items don't need a tracking number
	requiresTracking: boolean;
}

export interface ShipmentAllocation {
	shipmentId: string;
	quantity: number;
}

type OrderItemSize = NonNullable<GQLOrderItemTileFragment['sizes']>[number];

/**
 * How one size's quantity is spread across warehouse shipments. The warehouse can split a single
 * size down the middle - 5 of the 10 mediums in one parcel and 5 in another - so this is stored on
 * the size as a `{ shipmentId: quantity }` map rather than a single id on the line item.
 */
export function getSizeShipmentAllocations(size?: Pick<OrderItemSize, 'shipments'> | null): ShipmentAllocation[] {
	const shipments = size?.shipments;
	if (!shipments || typeof shipments !== 'object' || Array.isArray(shipments)) return [];

	return Object.entries(shipments)
		.map(([shipmentId, quantity]) => ({ shipmentId, quantity: Math.floor(Number(quantity)) || 0 }))
		.filter(allocation => !!allocation.shipmentId && allocation.quantity > 0);
}

export function getSizeAllocatedQuantity(size?: Pick<OrderItemSize, 'shipments'> | null): number {
	return getSizeShipmentAllocations(size).reduce((total, allocation) => total + allocation.quantity, 0);
}

//Quantity the warehouse hasn't told us about yet, or has left out of every shipment
export function getSizeUnallocatedQuantity(size?: Pick<OrderItemSize, 'shipments' | 'quantity'> | null): number {
	if (!size) return 0;
	return Math.max(0, size.quantity - getSizeAllocatedQuantity(size));
}

export function orderItemIsSplitAcrossShipments(item?: GQLOrderItemTileFragment | null): boolean {
	const shipmentIds = new Set<string>();
	item?.sizes?.forEach(size => {
		getSizeShipmentAllocations(size).forEach(allocation => shipmentIds.add(allocation.shipmentId));
		if (getSizeUnallocatedQuantity(size) > 0) shipmentIds.add('');
	});
	return shipmentIds.size > 1;
}

/**
 * Where a line item physically ships from. SCR and Trends items are decorated by outsourced partners and go out
 * separately from anything the warehouse holds.
 */
export function getOrderItemShipmentType(item?: GQLOrderItemTileFragment | null): GQLOrderTrackingTypeType {
	if (!orderItemIsExternallyFulfilled(item)) return GQLOrderTrackingTypeType.Warehouse;
	//A Trends garment ships with the Trends group even when it carries an SCR print
	return orderItemIsTrends(item) ? GQLOrderTrackingTypeType.Trends : GQLOrderTrackingTypeType.Scr;
}

export function getOrderShipmentKey(type: GQLOrderTrackingTypeType, shipmentId?: string | null) {
	return type === GQLOrderTrackingTypeType.Warehouse ? shipmentId || '' : type;
}

export function getOrderItemShipmentKey(item?: GQLOrderItemTileFragment | null) {
	return getOrderShipmentKey(getOrderItemShipmentType(item), item?.shipmentId);
}

const groupOrder = [GQLOrderTrackingTypeType.Warehouse, GQLOrderTrackingTypeType.Scr, GQLOrderTrackingTypeType.Trends];

//How much of a line item ships in one parcel, so its price can follow its quantity
function getShareOfAmount(amount?: string | number | null, units = 0, totalUnits = 0): number {
	const value = typeof amount === 'string' ? parseFloat(amount) : amount || 0;
	if (isNaN(value) || !totalUnits) return 0;
	return Math.round(value * (units / totalUnits) * 100) / 100;
}

interface ItemProjection {
	key: string;
	type: GQLOrderTrackingTypeType;
	shipmentId: string;
	quantityBySizeId: Map<string, number>;
	units: number;
}

//Which parcels a line item is spread over, and how much of it is in each
function getItemProjections(item: GQLOrderItemTileFragment): ItemProjection[] {
	const type = getOrderItemShipmentType(item);
	const sizes = item.sizes || [];
	const allUnits = sizes.reduce((total, size) => total + size.quantity, 0);

	//Outsourced groups aren't shipments, and an order the warehouse hasn't split has nothing to
	//spread - either way the whole item goes in one group
	const allocated = type === GQLOrderTrackingTypeType.Warehouse && sizes.some(size => getSizeShipmentAllocations(size).length);

	if (!allocated) {
		const shipmentId = type === GQLOrderTrackingTypeType.Warehouse ? item.shipmentId || '' : '';
		return [
			{
				key: getOrderShipmentKey(type, shipmentId),
				type,
				shipmentId,
				quantityBySizeId: new Map(sizes.map(size => [size.id, size.quantity])),
				units: allUnits,
			},
		];
	}

	const projections = new Map<string, ItemProjection>();

	const add = (shipmentId: string, sizeId: string, quantity: number) => {
		if (quantity <= 0) return;
		const key = getOrderShipmentKey(type, shipmentId);
		const projection =
			projections.get(key) || ({ key, type, shipmentId, quantityBySizeId: new Map(), units: 0 } satisfies ItemProjection);
		projection.quantityBySizeId.set(sizeId, (projection.quantityBySizeId.get(sizeId) || 0) + quantity);
		projection.units += quantity;
		projections.set(key, projection);
	};

	sizes.forEach(size => {
		getSizeShipmentAllocations(size).forEach(allocation => add(allocation.shipmentId, size.id, allocation.quantity));
		//Anything the warehouse hasn't placed sits in its own untracked group rather than riding along
		//with a parcel that may already have gone out
		add('', size.id, getSizeUnallocatedQuantity(size));
	});

	return [...projections.values()];
}

//The item as it appears in one parcel: only the sizes and quantities it holds, priced to match
function projectItem(item: GQLOrderItemTileFragment, projection: ItemProjection, share: { subtotal: number; total: number }) {
	return {
		...item,
		shipmentId: projection.shipmentId,
		subtotal: share.subtotal.toFixed(2),
		total: share.total.toFixed(2),
		sizes: (item.sizes || [])
			.filter(size => (projection.quantityBySizeId.get(size.id) || 0) > 0)
			.map(size => ({ ...size, quantity: projection.quantityBySizeId.get(size.id) || 0 })),
	} satisfies GQLOrderItemTileFragment;
}

/**
 * Splits an order's line items into the parcels they will go out in - one per Fulfill Engine
 * shipment, one for the screen printed items and one for the Trends items - with the tracking
 * number recorded against each.
 *
 * A line item whose quantity the warehouse has split appears in every parcel that holds part of it,
 * carrying only that parcel's sizes and quantities and its share of the price, so the groups still
 * add up to the order.
 */
export function getOrderShipmentGroups(
	items?: GQLOrderItemTileFragment[] | null,
	tracking?: GQLOrderTrackingFragment[] | null,
): OrderShipmentGroup[] {
	const groups: OrderShipmentGroup[] = [];

	items?.forEach(item => {
		const projections = getItemProjections(item);
		const allUnits = projections.reduce((total, projection) => total + projection.units, 0);
		let subtotalTaken = 0;
		let totalTaken = 0;

		projections.forEach((projection, index) => {
			//The last parcel picks up whatever rounding left behind, so the parts sum to the whole
			const last = index === projections.length - 1;
			const subtotal = last
				? (parseFloat(item.subtotal) || 0) - subtotalTaken
				: getShareOfAmount(item.subtotal, projection.units, allUnits);
			const total = last ? (parseFloat(item.total) || 0) - totalTaken : getShareOfAmount(item.total, projection.units, allUnits);
			subtotalTaken += subtotal;
			totalTaken += total;

			const projected = projectItem(item, projection, { subtotal, total });
			const group = groups.find(existing => existing.key === projection.key);

			if (group) {
				group.items.push(projected);
				group.requiresTracking = group.requiresTracking || !item.cancelled;
			} else {
				groups.push({
					key: projection.key,
					type: projection.type,
					shipmentId: projection.shipmentId,
					items: [projected],
					tracking: getShipmentTracking(tracking, projection.type, projection.shipmentId),
					requiresTracking: !item.cancelled,
				});
			}
		});
	});

	//Warehouse shipments first, then the outsourced groups
	return groups.sort((a, b) => {
		if (a.type !== b.type) return groupOrder.indexOf(a.type) - groupOrder.indexOf(b.type);
		if (!a.shipmentId) return 1;
		if (!b.shipmentId) return -1;
		return a.shipmentId > b.shipmentId ? 1 : -1;
	});
}

export function getShipmentTracking(
	tracking?: GQLOrderTrackingFragment[] | null,
	type?: GQLOrderTrackingTypeType,
	shipmentId?: string | null,
) {
	return tracking?.find(
		entry =>
			entry.type === type &&
			(type !== GQLOrderTrackingTypeType.Warehouse || (entry.shipmentId || '') === (shipmentId || '')) &&
			!!entry.trackingNumber,
	);
}

export function orderIsSplitShipment(items?: GQLOrderItemTileFragment[] | null): boolean {
	return getOrderShipmentGroups(items).length > 1;
}

export function getOrderShipmentsAwaitingTracking(
	items?: GQLOrderItemTileFragment[] | null,
	tracking?: GQLOrderTrackingFragment[] | null,
): OrderShipmentGroup[] {
	return getOrderShipmentGroups(items, tracking).filter(group => group.requiresTracking && !group.tracking);
}

/**
 * An order is only shipped once every parcel it goes out in has a tracking number against it.
 */
export function orderTrackingIsComplete(items?: GQLOrderItemTileFragment[] | null, tracking?: GQLOrderTrackingFragment[] | null): boolean {
	const groups = getOrderShipmentGroups(items, tracking).filter(group => group.requiresTracking);
	return !!groups.length && groups.every(group => !!group.tracking);
}

/**
 * Some parcels are on their way but the order isn't fully out the door yet - it sits between
 * processing and shipped rather than at a status of its own.
 */
export function orderIsPartiallyShipped(order?: Pick<GQLOrderFragment, 'items' | 'tracking' | 'status'> | null): boolean {
	if (!order || [GQLOrderStatusType.Shipped, GQLOrderStatusType.Delivered, GQLOrderStatusType.Cancelled].includes(order.status)) {
		return false;
	}
	return !!order.tracking?.some(entry => !!entry.trackingNumber) && !orderTrackingIsComplete(order.items, order.tracking);
}
